import { useState, useEffect } from 'react';
import ConfirmDeleteModal from '../../../components/ConfirmDeleteModal';
import ImageSourceModal from '../../../components/ui/ImageSourceModal';
import MultiSelectAutocomplete from '../../../components/ui/MultiSelectAutocomplete';
import { useImageCapture } from '../../../hooks/useImageCapture.jsx';

const BibleForm = ({ 
  editingBible = null, 
  onSave = () => {}, 
  onCancel = () => {},
  mode = 'create', // 'create', 'edit'
  addBible,
  updateBible,
  deleteBible
}) => {
  
  const [formData, setFormData] = useState({
    title: '',
    price: '',
    price_cover: '',
    total_with_cover: '',
    price_tab_index: '',
    total_with_tab_index: '',
    total_with_both: '',
    description: '',
    format: '',
    pages: '',
    version: '',
    language: '',
    available: true,
    has_cover: true,
    has_tab_index: false,
    characters_bible: [],
    images: [],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Hook para captura de imágenes
  const {
    isSourceModalOpen,
    openImageSourceModal,
    closeImageSourceModal,
    handleImageSourceSelect,
    createHiddenInputs
  } = useImageCapture();

  // Cargar datos de la biblia en modo edición
  useEffect(() => {
    if (editingBible && mode === 'edit') {
      setFormData({
        title: editingBible.title_bible || '',
        price: editingBible.price_bible || '',
        price_cover: editingBible.price_cover || '',
        total_with_cover: editingBible.total_with_cover || '',
        price_tab_index: editingBible.price_tab_index || '',
        total_with_tab_index: editingBible.total_with_tab_index || '',
        total_with_both: editingBible.total_with_both || '',
        description: editingBible.description_bible || '',
        format: editingBible.format_bible || '',
        pages: editingBible.pages_bible || '',
        version: editingBible.version_bible || '',
        language: editingBible.language_bible || '',
        available: editingBible.available_bible === true,
        has_cover: editingBible.has_cover === true,
        has_tab_index: editingBible.has_tab_index === true,
        characters_bible: editingBible.characters_bible || [],
        images: editingBible.image_urls || [],
      });
    }
  }, [editingBible, mode]);

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Limpiar mensajes previos
    setError(null);
    setSuccess(null);

    // Validar que al menos una imagen esté presente
    const processedImages = formData.images.filter(img => img);
    if (processedImages.length === 0) {
      setError('Debe agregar al menos una imagen de la biblia');
      return;
    }

    setIsSubmitting(true);
    try {
      const processedFormData = {
        ...formData,
        images: processedImages,
        available: formData.available,
        price_cover: formData.has_cover ? parseFloat(formData.price_cover) || 0 : 0,
        total_with_cover: formData.has_cover ? parseFloat(formData.total_with_cover) || 0 : 0,
        price_tab_index: formData.has_tab_index ? parseFloat(formData.price_tab_index) || 0 : 0,
        total_with_tab_index: formData.has_tab_index ? parseFloat(formData.total_with_tab_index) || 0 : 0,
        total_with_both: (formData.has_cover && formData.has_tab_index) ? parseFloat(formData.total_with_both) || 0 : 0,
        has_tab_index: formData.has_tab_index,
        characters_bible: formData.characters_bible
      };

      if (mode === 'edit' && editingBible) {
        await updateBible(editingBible.bible_id, {
          ...processedFormData,
          image_urls: processedImages
        });
        setSuccess('Biblia actualizada exitosamente');
        
        // Cerrar modal y recargar página después de un breve delay
        setTimeout(() => {
          onSave();
          // Recargar catálogo principal si está disponible
          if (window.refreshBookCatalog) {
            window.refreshBookCatalog();
          }
          window.location.reload();
        }, 1500);
      } else {
        await addBible({
          ...processedFormData,
          image_urls: processedImages
        });
        setSuccess('Biblia creada exitosamente');
        
        // Cerrar modal después de un breve delay para mostrar el mensaje
        setTimeout(() => {
          onSave();
          // Recargar catálogo principal si está disponible
          if (window.refreshBookCatalog) {
            window.refreshBookCatalog();
          }
        }, 1500);
      }
    } catch (error) {
      console.error('Error saving bible:', error);
      setError(error.message || 'Error al guardar la biblia. Por favor, intente nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Manejar eliminación
  const handleDelete = async () => {
    if (!editingBible || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      await deleteBible(editingBible.bible_id);
      onSave();
    } catch (error) {
      console.error('Error deleting bible:', error);
    } finally {
      setIsSubmitting(false);
      setShowDeleteConfirm(false);
    }
  };

  const [imageIndex, setImageIndex] = useState(0);

  // Función para convertir archivo a base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleChange = async (e) => {
    const { name, value, files } = e.target;
    if (name === 'images') {
      const selectedFiles = Array.from(files).slice(0, 5);
      
      // Convertir archivos a Data URLs (Base64)
      const imageUrls = await Promise.all(
        selectedFiles.map(file => convertToBase64(file))
      );
      
      setFormData((prev) => ({ ...prev, images: imageUrls }));
      setImageIndex(0);
    } else {
      const newFormData = { ...formData, [name]: value };
      
      // Calcular totales automáticamente
      if (name === 'price' || name === 'price_cover' || name === 'price_tab_index') {
        const basePrice = parseFloat(name === 'price' ? value : newFormData.price) || 0;
        const coverPrice = parseFloat(name === 'price_cover' ? value : newFormData.price_cover) || 0;
        const tabIndexPrice = parseFloat(name === 'price_tab_index' ? value : newFormData.price_tab_index) || 0;
        
        // Total con forro
        newFormData.total_with_cover = (basePrice + coverPrice).toFixed(2);
        
        // Total con índice
        newFormData.total_with_tab_index = (basePrice + tabIndexPrice).toFixed(2);
        
        // Total con ambos (forro + índice)
        newFormData.total_with_both = (basePrice + coverPrice + tabIndexPrice).toFixed(2);
      }
      
      setFormData(newFormData);
    }
  };

  // Función para remover imagen
  const removeImage = (index) => {
    const updatedImages = [...formData.images];
    updatedImages[index] = null;
    // Si se elimina la portada (índice 0), también eliminar del marco 1
    if (index === 0) {
      updatedImages[1] = null;
    }
    // Si se elimina del marco 1, también eliminar la portada
    if (index === 1) {
      updatedImages[0] = null;
    }
    setFormData((prev) => ({ ...prev, images: updatedImages }));
  };

  return (
    <div>
      <form
        onSubmit={handleSubmit}
        className="bg-white border border-slate-200 rounded-xl p-6 shadow-md space-y-6 max-w-5xl mx-auto"
    >
      {/* Mensajes de error y éxito */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-center">
            <div className="text-red-600 text-sm">{error}</div>
          </div>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div className="flex items-center">
            <div className="text-green-600 text-sm">{success}</div>
          </div>
        </div>
      )}

      {/* IMAGEN + CAMPOS LATERALES */}
      <div className="flex flex-wrap gap-6">
        {/* Marcos opcionales */}
        <div className="flex flex-wrap gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="relative w-[100px] h-[120px] border border-dashed border-slate-300 rounded-md bg-slate-50 overflow-hidden flex items-center justify-center"
            >
              <div
                onClick={() => openImageSourceModal((base64Url) => {
                  const updatedImages = [...formData.images];
                  updatedImages[i] = base64Url;
                  // Si es el marco 1 (Portada), también actualizar el índice 0
                  if (i === 1) updatedImages[0] = base64Url;
                  setFormData((prev) => ({ ...prev, images: updatedImages }));
                })}
                className="absolute inset-0 cursor-pointer"
              />
              {(i === 1 ? formData.images[0] : formData.images[i]) ? (
                <>
                  <img
                    src={i === 1 ? formData.images[0] : formData.images[i]}
                    alt={`extra-${i}`}
                    className="w-full h-full object-cover rounded"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs font-bold hover:bg-red-600 transition-colors shadow-md"
                  >
                    ×
                  </button>
                </>
              ) : (
                <>
                  <span className="text-slate-400 text-xl">+</span>
                  <span className="absolute bottom-1 text-[10px] text-slate-400">
                    {i === 1 ? 'Portada' : 'Opcional'}
                  </span>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Imagen de Portada */}
        <div className="relative w-[250px] h-[300px] border border-slate-300 rounded-md overflow-hidden flex items-center justify-center"
             style={{
               backgroundImage: 'url(/assets/images/background_image.png)',
               backgroundSize: 'cover',
               backgroundPosition: 'center',
               backgroundRepeat: 'no-repeat'
             }}>
          <div
            onClick={() => openImageSourceModal((base64Url) => {
              const updatedImages = [...formData.images];
              updatedImages[0] = base64Url;
              updatedImages[1] = base64Url; // reflejo en primer marco opcional (Portada)
              setFormData((prev) => ({ ...prev, images: updatedImages }));
            })}
            className="absolute inset-0 cursor-pointer"
          />
          {formData.images[0] ? (
            <>
              <div className="relative w-[120px] h-[155px] flex items-center justify-center">
                <img
                  src={formData.images[0]}
                  alt="Imagen de Portada"
                  className="w-full h-full object-cover rounded shadow-lg"
                />
              </div>
              <button
                type="button"
                onClick={() => removeImage(0)}
                className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold hover:bg-red-600 transition-colors shadow-md"
              >
                ×
              </button>
            </>
          ) : (
            <span className="text-white text-sm pointer-events-none text-center px-4 bg-black bg-opacity-50 rounded p-2">
              Imagen de Portada
            </span>
          )}
        </div>

        {/* Inputs de Título a Versión */}
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { label: 'Título', name: 'title' },
            { label: formData.has_cover ? 'Precio Base' : 'Precio', name: 'price', type: 'number' },
            { label: 'Páginas', name: 'pages', type: 'number' },
          ].map(({ label, name, type = 'text' }) => (
            <div key={name}>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {label}
              </label>
              <input
                type={type}
                name={name}
                value={formData[name]}
                onChange={handleChange}
                required
                placeholder={`Ingrese ${label.toLowerCase()}`}
                className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm text-slate-800"
              />
            </div>
          ))}


          {/* Formato */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Formato
            </label>
            <select
              name="format"
              value={formData.format}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm text-slate-800"
            >
              <option value="">Seleccionar formato</option>
              <option value="Tapa dura">Tapa dura</option>
              <option value="Tapa blanda">Tapa blanda</option>
            </select>
          </div>

          {/* Versión */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Versión
            </label>
            <select
              name="version"
              value={formData.version}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm text-slate-800"
            >
              <option value="">Seleccionar versión</option>
              <option value="Reina-Valera 1960">Reina-Valera 1960</option>
              <option value="Reina-Valera Revisada (RVR)">Reina-Valera Revisada (RVR)</option>
              <option value="Reina Valera Contemporánea (RVC)">Reina Valera Contemporánea (RVC)</option>
              <option value="Nueva Versión Internacional (NVI)">Nueva Versión Internacional (NVI)</option>
              <option value="Dios Habla Hoy (DHH)">Dios Habla Hoy (DHH)</option>
              <option value="Biblia de las Américas (LBLA)">Biblia de las Américas (LBLA)</option>
              <option value="Nueva Biblia de los Hispanos (NBLA)">Nueva Biblia de los Hispanos (NBLA)</option>
              <option value="Palabra de Dios para Todos (PDT)">Palabra de Dios para Todos (PDT)</option>
              <option value="Traducción en Lenguaje Actual (TLA)">Traducción en Lenguaje Actual (TLA)</option>
              <option value="Nueva Traducción Viviente (NTV)">Nueva Traducción Viviente (NTV)</option>
              <option value="Biblia del Jubileo 2000">Biblia del Jubileo 2000</option>
              <option value="Biblia Textual">Biblia Textual</option>
              <option value="Biblia Interconfesional">Biblia Interconfesional</option>
              <option value="Biblia Latinoamericana">Biblia Latinoamericana</option>
              <option value="Biblia de Jerusalén">Biblia de Jerusalén</option>
              <option value="La Palabra (Hispanoamérica)">La Palabra (Hispanoamérica)</option>
              <option value="Biblia al Día">Biblia al Día</option>
              <option value="Biblia Kadosh Israelita Mesiánica">Biblia Kadosh Israelita Mesiánica</option>
              <option value="Biblia Peshitta">Biblia Peshitta</option>
              <option value="Biblia del Pueblo de Dios">Biblia del Pueblo de Dios</option>
            </select>
          </div>

          {/* Idioma */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Idioma
            </label>
            <select
              name="language"
              value={formData.language}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm text-slate-800"
            >
              <option value="">Seleccionar idioma</option>
              <option value="Español">Español</option>
              <option value="Inglés">Inglés</option>
              <option value="Bilingüe">Bilingüe</option>
            </select>
          </div>
        </div>
      </div>

      {/* DESCRIPCIÓN */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Descripción
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          required
          rows={4}
          maxLength={360}
          className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm text-slate-800 resize-none"
          placeholder="Descripción de la biblia..."
        />
        <div className="text-right text-xs text-slate-500 mt-1">
          {formData.description.length}/360 caracteres
        </div>
      </div>

      {/* CARACTERÍSTICAS DE LA BIBLIA */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Características de la Biblia
        </label>
        <div className="space-y-2">
          {/* Tags Display */}
          {formData.characters_bible.length > 0 && (
            <div className="flex flex-wrap gap-2 p-2 border border-slate-200 rounded-md bg-slate-50">
              {formData.characters_bible.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm"
                >
                  #{tag}
                  <button
                    type="button"
                    onClick={() => {
                      const updatedTags = formData.characters_bible.filter((_, i) => i !== index);
                      setFormData(prev => ({ ...prev, characters_bible: updatedTags }));
                    }}
                    className="ml-1 text-blue-600 hover:text-blue-800 font-bold"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          
          {/* Input Field */}
          <input
            type="text"
            placeholder="Escribir características y presionar Enter o coma..."
            className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm text-slate-800"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                const value = e.target.value.trim();
                if (value && !formData.characters_bible.includes(value)) {
                  setFormData(prev => ({
                    ...prev,
                    characters_bible: [...prev.characters_bible, value]
                  }));
                  e.target.value = '';
                }
              }
            }}
            onBlur={(e) => {
              const value = e.target.value.trim();
              if (value && !formData.characters_bible.includes(value)) {
                setFormData(prev => ({
                  ...prev,
                  characters_bible: [...prev.characters_bible, value]
                }));
                e.target.value = '';
              }
            }}
          />
        </div>
        <div className="text-xs text-slate-500 mt-1">
          Escriba las características y presione Enter o coma para crear hashtags
        </div>
      </div>

      {/* DISPONIBILIDAD Y CHECKBOXES */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Disponibilidad
          </label>
          <select
            name="available"
            value={formData.available.toString()}
            onChange={(e) => setFormData(prev => ({ ...prev, available: e.target.value === 'true' }))}
            className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm text-slate-800"
          >
            <option value="true">Disponible</option>
            <option value="false">No disponible</option>
          </select>
        </div>

        {/* Checkboxes para Forro e Índice */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Checkbox para Forro */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="has_cover"
              checked={formData.has_cover}
              onChange={(e) => {
                const hasCover = e.target.checked;
                setFormData(prev => ({
                  ...prev,
                  has_cover: hasCover,
                  price_cover: hasCover ? prev.price_cover : '0',
                  total_with_cover: hasCover ? prev.total_with_cover : '0'
                }));
              }}
              className="h-4 w-4 text-primary focus:ring-primary border-slate-300 rounded"
            />
            <label htmlFor="has_cover" className="text-sm text-slate-700">
              Tiene forro
            </label>
          </div>

          {/* Checkbox para Índice */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="has_tab_index"
              checked={formData.has_tab_index}
              onChange={(e) => {
                const hasTabIndex = e.target.checked;
                setFormData(prev => ({
                  ...prev,
                  has_tab_index: hasTabIndex,
                  price_tab_index: hasTabIndex ? prev.price_tab_index : '0',
                  total_with_tab_index: hasTabIndex ? prev.total_with_tab_index : '0'
                }));
              }}
              className="h-4 w-4 text-primary focus:ring-primary border-slate-300 rounded"
            />
            <label htmlFor="has_tab_index" className="text-sm text-slate-700">
              Tiene pestañas
            </label>
          </div>
        </div>
      </div>

      {/* Campos de precios del forro - Solo si tiene forro */}
      {formData.has_cover && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Precio del Forro
            </label>
            <input
              type="number"
              name="price_cover"
              value={formData.price_cover}
              onChange={handleChange}
              step="0.01"
              min="0"
              placeholder="Ingrese precio del forro"
              className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm text-slate-800"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Total con Forro
            </label>
            <input
              type="number"
              name="total_with_cover"
              value={formData.total_with_cover}
              readOnly
              className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm bg-slate-100 text-slate-600 text-sm cursor-not-allowed"
              placeholder="Se calcula automáticamente"
            />
          </div>
        </div>
      )}

      {/* Campos de precios del índice - Solo si tiene índice */}
      {formData.has_tab_index && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Precio de la pestaña
            </label>
            <input
              type="number"
              step="0.01"
              name="price_tab_index"
              value={formData.price_tab_index}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm text-slate-800"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Total de la pestaña
            </label>
            <input
              type="number"
              name="total_with_tab_index"
              value={formData.total_with_tab_index}
              readOnly
              className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm bg-slate-100 text-slate-600 text-sm cursor-not-allowed"
              placeholder="Se calcula automáticamente"
            />
          </div>
        </div>
      )}

      {/* Campo de precio total combinado - Solo si tiene ambos */}
      {formData.has_cover && formData.has_tab_index && (
        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Total con Forro + Índice
              </label>
              <input
                type="number"
                name="total_with_both"
                value={formData.total_with_both}
                readOnly
                className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm bg-slate-100 text-slate-600 text-sm cursor-not-allowed"
                placeholder="Se calcula automáticamente"
              />
              <p className="text-xs text-slate-500 mt-1">
                Precio base + forro + índice = ${formData.total_with_both}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* BOTONES */}
      <div className="pt-4 flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-primary text-primary-foreground px-5 py-2.5 rounded-lg font-medium shadow-md hover:bg-primary/90 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed text-center"
          >
            {isSubmitting ? 'Guardando...' : (mode === 'edit' ? 'Actualizar biblia' : 'Crear biblia')}
          </button>
          
          <button
            type="button"
            onClick={onCancel}
            className="bg-slate-200 text-slate-700 px-5 py-2.5 rounded-lg font-medium shadow-md hover:bg-slate-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400 text-center"
          >
            Cancelar
          </button>
        </div>

        {mode === 'edit' && editingBible && (
          <div className="w-full sm:w-auto">
            {showDeleteConfirm ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isSubmitting}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg font-medium shadow-md hover:bg-red-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 text-center"
                >
                  {isSubmitting ? 'Eliminando...' : 'Confirmar'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="bg-slate-200 text-slate-700 px-4 py-2 rounded-lg font-medium shadow-md hover:bg-slate-300 transition-all duration-200 text-center"
                >
                  Cancelar
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="w-full sm:w-auto bg-orange-600 text-white px-4 py-2 rounded-lg font-medium shadow-md hover:bg-orange-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 text-center"
              >
                Eliminar biblia
              </button>
            )}
          </div>
        )}
      </div>

      </form>

      {/* Modal de confirmación de eliminación */}
      <ConfirmDeleteModal
        isOpen={showDeleteConfirm}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        title="¿Eliminar biblia?"
        message="Esta acción no se puede deshacer."
        itemName={editingBible?.title_bible}
        isLoading={isSubmitting}
      />

      {/* Modal de selección de fuente de imagen */}
      <ImageSourceModal
        isOpen={isSourceModalOpen}
        onClose={closeImageSourceModal}
        onSelectSource={handleImageSourceSelect}
      />

      {/* Inputs ocultos para captura de imágenes */}
      {createHiddenInputs()}
    </div>
  );
};

export default BibleForm;

